import React, { useMemo, useState } from 'react';
import { MdArrowDownward, MdArrowUpward, MdWorkspaces } from 'react-icons/md';

import { styledDs as styled } from '../../../styles/theme';
import { Table, TableWrap } from '../Table';
import { DsSkeleton, DsEmptyState } from '../Feedback';

export type WorkQueueColumn<T> = {
  key: string;
  label: string;
  render: (item: T) => React.ReactNode;
  /** Torna a coluna clicável para ordenar. Devolve a chave usada na comparação. */
  sortValue?: (item: T) => string | number;
  /** Torna a coluna clicável para agrupar. Devolve o nome do grupo. */
  groupValue?: (item: T) => string;
};

export type WorkQueueProps<T> = {
  items: T[];
  columns: WorkQueueColumn<T>[];
  itemKey: (item: T) => string | number;
  renderActions?: (item: T) => React.ReactNode;
  loading?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  /** Nomeia a região rolável da tabela; sem isso o teclado rola algo sem nome. */
  label: string;
};

type DirecaoOrdenacao = 'asc' | 'desc';

const ActionsWrap = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`;

const HeaderButton = styled.button<{ $ativo: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme, $ativo }) => ($ativo ? theme.colors.accent : 'inherit')};
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const GroupRow = styled.tr`
  th {
    padding: 6px 12px;
    background: ${({ theme }) => theme.colors.page};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: ${({ theme }) => theme.font.sizeSm};
    font-weight: ${({ theme }) => theme.weight.semibold};
    text-align: left;
    text-transform: none;
    letter-spacing: 0;
  }

  th span {
    margin-left: 6px;
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: ${({ theme }) => theme.weight.normal};
  }
`;

const SKELETON_ROWS = [0, 1, 2];

const SEM_GRUPO = 'Sem módulo';

export function ordenarItens<T>(
  itens: T[],
  valorDe: (item: T) => string | number,
  direcao: DirecaoOrdenacao
): T[] {
  const sinal = direcao === 'asc' ? 1 : -1;

  return [...itens].sort((a, b) => {
    const valorA = valorDe(a);
    const valorB = valorDe(b);

    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return (valorA - valorB) * sinal;
    }

    return String(valorA).localeCompare(String(valorB), 'pt-BR') * sinal;
  });
}

export function agruparItens<T>(
  itens: T[],
  grupoDe: (item: T) => string
): Array<{ grupo: string; itens: T[] }> {
  const grupos = new Map<string, T[]>();

  itens.forEach((item) => {
    const nome = grupoDe(item)?.trim() || SEM_GRUPO;

    if (!grupos.has(nome)) grupos.set(nome, []);

    grupos.get(nome)!.push(item);
  });

  return Array.from(grupos.entries())
    .map(([grupo, itensDoGrupo]) => ({ grupo, itens: itensDoGrupo }))
    .sort((a, b) => a.grupo.localeCompare(b.grupo, 'pt-BR'));
}

export function WorkQueue<T>({
  items,
  columns,
  itemKey,
  renderActions,
  loading = false,
  emptyTitle,
  emptyDescription,
  label,
}: WorkQueueProps<T>) {
  const [ordenacao, setOrdenacao] = useState<{ coluna: string; direcao: DirecaoOrdenacao } | null>(
    null
  );
  const [agrupamento, setAgrupamento] = useState<string | null>(null);

  const colunaOrdenada = columns.find((coluna) => coluna.key === ordenacao?.coluna);
  const colunaAgrupada = columns.find((coluna) => coluna.key === agrupamento);

  const ordenados = useMemo(() => {
    if (!colunaOrdenada?.sortValue || !ordenacao) return items;

    return ordenarItens(items, colunaOrdenada.sortValue, ordenacao.direcao);
  }, [items, colunaOrdenada, ordenacao]);

  const grupos = useMemo(() => {
    if (!colunaAgrupada?.groupValue) return null;

    return agruparItens(ordenados, colunaAgrupada.groupValue);
  }, [ordenados, colunaAgrupada]);

  function alternarOrdenacao(chave: string) {
    setOrdenacao((atual) =>
      atual?.coluna === chave
        ? { coluna: chave, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
        : { coluna: chave, direcao: 'asc' }
    );
  }

  function alternarAgrupamento(chave: string) {
    setAgrupamento((atual) => (atual === chave ? null : chave));
  }

  /**
   * O leitor de tela anuncia a ordenação por aqui; a setinha é `aria-hidden` e
   * só serve para quem enxerga.
   */
  function ordemDaColuna(
    coluna: WorkQueueColumn<T>
  ): 'ascending' | 'descending' | 'none' | undefined {
    if (!coluna.sortValue) return undefined;
    if (ordenacao?.coluna !== coluna.key) return 'none';

    return ordenacao.direcao === 'asc' ? 'ascending' : 'descending';
  }

  function cabecalho(coluna: WorkQueueColumn<T>): React.ReactNode {
    if (coluna.sortValue) {
      const ativo = ordenacao?.coluna === coluna.key;
      const crescente = ativo && ordenacao?.direcao === 'asc';

      return (
        <HeaderButton
          type="button"
          $ativo={ativo}
          title={`Ordenar por ${coluna.label}, ${crescente ? 'do maior para o menor' : 'do menor para o maior'}`}
          aria-label={`Ordenar por ${coluna.label}`}
          onClick={() => alternarOrdenacao(coluna.key)}
        >
          {coluna.label}
          {ativo &&
            (crescente ? (
              <MdArrowUpward aria-hidden="true" />
            ) : (
              <MdArrowDownward aria-hidden="true" />
            ))}
        </HeaderButton>
      );
    }

    if (coluna.groupValue) {
      const ativo = agrupamento === coluna.key;

      return (
        <HeaderButton
          type="button"
          $ativo={ativo}
          title={ativo ? `Desagrupar por ${coluna.label}` : `Agrupar por ${coluna.label}`}
          aria-label={`Agrupar por ${coluna.label}`}
          onClick={() => alternarAgrupamento(coluna.key)}
        >
          {coluna.label}
          {ativo && <MdWorkspaces aria-hidden="true" />}
        </HeaderButton>
      );
    }

    return coluna.label;
  }

  function linha(item: T): React.ReactNode {
    return (
      <tr key={itemKey(item)}>
        {columns.map((column) => (
          <td key={column.key}>{column.render(item)}</td>
        ))}
        {renderActions && (
          <td>
            <ActionsWrap>{renderActions(item)}</ActionsWrap>
          </td>
        )}
      </tr>
    );
  }

  if (loading) {
    return (
      <TableWrap role="region" aria-label={label} aria-busy="true" tabIndex={0}>
        <Table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
              {renderActions && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {SKELETON_ROWS.map((row) => (
              <tr key={`skeleton-${row}`}>
                {columns.map((column) => (
                  <td key={column.key}>
                    <DsSkeleton $width="70%" />
                  </td>
                ))}
                {renderActions && (
                  <td>
                    <DsSkeleton $width="120px" $height="28px" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    );
  }

  if (items.length === 0) {
    return (
      <TableWrap>
        <DsEmptyState title={emptyTitle} description={emptyDescription} />
      </TableWrap>
    );
  }

  const colunasNoTotal = columns.length + (renderActions ? 1 : 0);

  return (
    <TableWrap role="region" aria-label={label} tabIndex={0}>
      <Table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} aria-sort={ordemDaColuna(column)}>
                {cabecalho(column)}
              </th>
            ))}
            {renderActions && <th style={{ textAlign: 'right' }}>Ações</th>}
          </tr>
        </thead>
        {grupos ? (
          grupos.map(({ grupo, itens }) => (
            <tbody key={grupo}>
              <GroupRow>
                <th colSpan={colunasNoTotal} scope="colgroup">
                  {grupo}
                  <span>
                    {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                  </span>
                </th>
              </GroupRow>
              {itens.map(linha)}
            </tbody>
          ))
        ) : (
          <tbody>{ordenados.map(linha)}</tbody>
        )}
      </Table>
    </TableWrap>
  );
}
